import React, { useEffect, useState } from 'react';
import { Checkbox, message, Spin } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';
import { useAppDispatch } from '../../hooks/redux';
import { changeNotificationsAction } from '../../redux/actions/userActions';
import { NotificationItem } from '../../models/user/user';
import { AccountPermissionTypes } from '../../models/permission/enums';
import { usePermissions } from '../../hooks/permissions';

import {
    ACCEPTS_SHARING,
    ADDS_PRODUCTS,
    defaultState,
    LEAVES_COMMENT,
    SEND_COPY,
    UPLOAD_DOCUMENT,
} from './NotificationsEmail.mockData';
import {
    CheckBoxContainer,
    NotificationsEmailTable,
    NotificationsEmailText,
    NotificationsTableTitle,
    NotificationTip,
    SectionTitle,
} from './NotificationsEmail.styles';
import { PageContainer } from '../../styles/common.styles';
import {
    getLiveGuidelineNotificationPreferencesAction,
    updateLiveGuidelineNotificationPreferenceAction,
} from '../../redux/actions/liveGuidelineActions';
import { LiveGuidelineNotificationPreference } from '../../redux/actions/liveGuidelineActions/types';

interface Props {
    userNotifications: Array<NotificationItem>;
    id: number | undefined;
}

interface State {
    [key: string]: NotificationItem;
}

const NotificationsEmail: React.FC<Props> = ({ userNotifications, id }) => {
    const dispatch = useAppDispatch();
    const { getAccountRole } = usePermissions();

    const [notifications, setNotifications] = useState<State>(defaultState);

    const [guidelineState, setGuidelineState] = useState<{
        pending: boolean;
        preferences: LiveGuidelineNotificationPreference[];
    }>({
        pending: true,
        preferences: [],
    });

    useEffect(() => {
        const currentNotifications = userNotifications.reduce(
            (agg, v) => ({ ...agg, [v.name]: v }),
            notifications,
        );

        setNotifications(currentNotifications);
    }, [userNotifications]);

    const handleEachTimeChange = (data: CheckboxChangeEvent) => {
        const value = notifications[data.target.name as any];

        value.sendEmailMeEachTime = data.target.checked;

        submitChange(data.target.name, value);
    };

    const handleDigestChange = (data: CheckboxChangeEvent) => {
        const value = notifications[data.target.name as any];

        value.sendEmailDigest = data.target.checked;

        submitChange(data.target.name, value);
    };

    const submitChange = (name: string = '', value: NotificationItem) => {
        setNotifications({ ...notifications, [name]: value });

        dispatch(
            changeNotificationsAction(
                Object.values({ ...notifications, [name]: value }).map((item) => ({
                    ...item,
                    userId: id,
                })),
            ),
        );
    };

    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const response = await dispatch(
                    getLiveGuidelineNotificationPreferencesAction(),
                ).unwrap();

                if (!response?.data) {
                    throw new Error('Live Guideline notification preferences were not returned.');
                }

                setGuidelineState({
                    pending: false,
                    preferences: response.data,
                });
            } catch {
                setGuidelineState((current) => ({
                    ...current,
                    pending: false,
                }));

                message.error('Unable to load Live Guideline notification preferences.');
            }
        };

        loadPreferences();
    }, [dispatch]);

    const handleGuidelinePreferenceChange = async (
        preference: LiveGuidelineNotificationPreference,
        field: 'displayOnDashboard' | 'sendEmailDigestDaily' | 'sendEmailDigest',
        checked: boolean,
    ) => {
        const updatedPreference = {
            ...preference,
            [field]: checked,
        };

        setGuidelineState((current) => ({
            ...current,
            preferences: current.preferences.map((item) =>
                item.liveGuidelineId === preference.liveGuidelineId ? updatedPreference : item,
            ),
        }));

        try {
            const response = await dispatch(
                updateLiveGuidelineNotificationPreferenceAction({
                    liveGuidelineId: preference.liveGuidelineId,
                    preference: {
                        displayOnDashboard: updatedPreference.displayOnDashboard,
                        sendEmailDigestDaily: updatedPreference.sendEmailDigestDaily,
                        sendEmailDigest: updatedPreference.sendEmailDigest,
                    },
                }),
            ).unwrap();

            if (!response?.data) {
                throw new Error('The updated notification preference was not returned.');
            }
        } catch {
            setGuidelineState((current) => ({
                ...current,
                preferences: current.preferences.map((item) =>
                    item.liveGuidelineId === preference.liveGuidelineId ? preference : item,
                ),
            }));

            message.error(
                `Unable to save notification preferences for ${preference.liveGuidelineName}.`,
            );
        }
    };

    return (
        <PageContainer>
            <SectionTitle>Project notifications</SectionTitle>

            <NotificationsEmailText style={{ paddingLeft: '0' }}>
                A record of project notifications is always available from each project’s
                Notifications tab.
            </NotificationsEmailText>

            <NotificationsEmailTable style={{ marginBottom: '50px' }}>
                <thead>
                    <tr>
                        <td>
                            <NotificationsTableTitle
                                style={{
                                    justifyContent: 'flex-start',
                                    paddingLeft: '0',
                                }}
                            >
                                Notify when
                            </NotificationsTableTitle>
                        </td>

                        <td>
                            <NotificationsTableTitle>Email me each time</NotificationsTableTitle>
                        </td>

                        <td>
                            <NotificationsTableTitle>
                                <NotificationTip>Sent every 24 hours</NotificationTip>
                                Email digest
                            </NotificationsTableTitle>
                        </td>
                    </tr>
                </thead>

                <tbody>
                    {getAccountRole() !== AccountPermissionTypes.ACCOUNT_FREE && (
                        <tr>
                            <td>{notifications[ACCEPTS_SHARING]?.name.toString()}</td>

                            <td>
                                <CheckBoxContainer>
                                    <Checkbox
                                        name={notifications[ACCEPTS_SHARING]?.name.toString()}
                                        checked={
                                            notifications[ACCEPTS_SHARING]?.sendEmailMeEachTime
                                        }
                                        onChange={handleEachTimeChange}
                                    />
                                </CheckBoxContainer>
                            </td>

                            <td>
                                <CheckBoxContainer>
                                    <Checkbox
                                        name={notifications[ACCEPTS_SHARING]?.name}
                                        checked={notifications[ACCEPTS_SHARING]?.sendEmailDigest}
                                        onChange={handleDigestChange}
                                    />
                                </CheckBoxContainer>
                            </td>
                        </tr>
                    )}

                    <tr>
                        <td>Someone adds, edits, or deletes a product in my projects</td>

                        <td>
                            <CheckBoxContainer>
                                <Checkbox
                                    name={notifications[ADDS_PRODUCTS]?.name.toString()}
                                    checked={notifications[ADDS_PRODUCTS]?.sendEmailMeEachTime}
                                    onChange={handleEachTimeChange}
                                />
                            </CheckBoxContainer>
                        </td>

                        <td>
                            <CheckBoxContainer>
                                <Checkbox
                                    name={notifications[ADDS_PRODUCTS]?.name.toString()}
                                    checked={notifications[ADDS_PRODUCTS]?.sendEmailDigest}
                                    onChange={handleDigestChange}
                                />
                            </CheckBoxContainer>
                        </td>
                    </tr>

                    <tr>
                        <td>Someone leaves a comment on one of my projects</td>

                        <td>
                            <CheckBoxContainer>
                                <Checkbox
                                    name={notifications[LEAVES_COMMENT]?.name.toString()}
                                    checked={notifications[LEAVES_COMMENT]?.sendEmailMeEachTime}
                                    onChange={handleEachTimeChange}
                                />
                            </CheckBoxContainer>
                        </td>

                        <td>
                            <CheckBoxContainer>
                                <Checkbox
                                    name={notifications[LEAVES_COMMENT]?.name.toString()}
                                    checked={notifications[LEAVES_COMMENT]?.sendEmailDigest}
                                    onChange={handleDigestChange}
                                />
                            </CheckBoxContainer>
                        </td>
                    </tr>

                    <tr>
                        <td>Someone uploads a new document for a product</td>

                        <td>
                            <CheckBoxContainer>
                                <Checkbox
                                    name={notifications[UPLOAD_DOCUMENT]?.name.toString()}
                                    checked={notifications[UPLOAD_DOCUMENT]?.sendEmailMeEachTime}
                                    onChange={handleEachTimeChange}
                                />
                            </CheckBoxContainer>
                        </td>

                        <td>
                            <CheckBoxContainer>
                                <Checkbox
                                    name={notifications[UPLOAD_DOCUMENT]?.name.toString()}
                                    checked={notifications[UPLOAD_DOCUMENT]?.sendEmailDigest}
                                    onChange={handleDigestChange}
                                />
                            </CheckBoxContainer>
                        </td>
                    </tr>

                    <tr>
                        <td>
                            I contact a manufacturer for spec & quote help: send me a copy of my
                            message
                        </td>

                        <td>
                            <CheckBoxContainer>
                                <Checkbox
                                    name={notifications[SEND_COPY]?.name.toString()}
                                    checked={notifications[SEND_COPY]?.sendEmailMeEachTime}
                                    onChange={handleEachTimeChange}
                                />
                            </CheckBoxContainer>
                        </td>
                    </tr>
                </tbody>
            </NotificationsEmailTable>

            <SectionTitle>Live Guideline notifications</SectionTitle>

            <NotificationsEmailText style={{ paddingLeft: '0' }}>
                A record of Live Guideline notifications is always available from each Live
                Guideline’s Notifications tab.
            </NotificationsEmailText>

            <NotificationsEmailTable aria-label="Live Guideline notification preferences">
                <thead>
                    <tr>
                        <td style={{ width: '34%' }}>
                            <NotificationsTableTitle
                                style={{
                                    justifyContent: 'flex-start',
                                    paddingLeft: '0',
                                }}
                            >
                                Notify of changes to these Live Guidelines
                            </NotificationsTableTitle>
                        </td>

                        <td style={{ width: '22%' }}>
                            <NotificationsTableTitle>Display on Dashboard</NotificationsTableTitle>
                        </td>

                        <td style={{ width: '22%' }}>
                            <NotificationsTableTitle>Email me each time</NotificationsTableTitle>
                        </td>

                        <td style={{ width: '22%' }}>
                            <NotificationsTableTitle>
                                <NotificationTip>Sent weekly</NotificationTip>
                                Email digest
                            </NotificationsTableTitle>
                        </td>
                    </tr>
                </thead>

                <tbody>
                    {guidelineState.pending ? (
                        <tr>
                            <td colSpan={4}>
                                <CheckBoxContainer>
                                    <Spin size="small" />
                                </CheckBoxContainer>
                            </td>
                        </tr>
                    ) : guidelineState.preferences.length === 0 ? (
                        <tr>
                            <td colSpan={4}>No Live Guidelines available.</td>
                        </tr>
                    ) : (
                        guidelineState.preferences.map((preference) => (
                            <tr key={preference.liveGuidelineId}>
                                <td>{preference.liveGuidelineName}</td>

                                <td>
                                    <CheckBoxContainer>
                                        <Checkbox
                                            aria-label={`Display ${preference.liveGuidelineName} notifications on Dashboard`}
                                            checked={preference.displayOnDashboard}
                                            onChange={(event) =>
                                                handleGuidelinePreferenceChange(
                                                    preference,
                                                    'displayOnDashboard',
                                                    event.target.checked,
                                                )
                                            }
                                        />
                                    </CheckBoxContainer>
                                </td>

                                <td>
                                    <CheckBoxContainer>
                                        <Checkbox
                                            aria-label={`Email ${preference.liveGuidelineName} notifications each time`}
                                            checked={preference.sendEmailDigestDaily}
                                            onChange={(event) =>
                                                handleGuidelinePreferenceChange(
                                                    preference,
                                                    'sendEmailDigestDaily',
                                                    event.target.checked,
                                                )
                                            }
                                        />
                                    </CheckBoxContainer>
                                </td>

                                <td>
                                    <CheckBoxContainer>
                                        <Checkbox
                                            aria-label={`Include ${preference.liveGuidelineName} notifications in email digest`}
                                            checked={preference.sendEmailDigest}
                                            onChange={(event) =>
                                                handleGuidelinePreferenceChange(
                                                    preference,
                                                    'sendEmailDigest',
                                                    event.target.checked,
                                                )
                                            }
                                        />
                                    </CheckBoxContainer>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </NotificationsEmailTable>
        </PageContainer>
    );
};

export default NotificationsEmail;
